import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ModuleProvider } from "./context/ModuleContext";
import { useEffect } from "react";
import { TutorialProvider } from "./components/TutorialSystem";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import ContactsPage from "./pages/ContactsPage";
import ContactProfilePage from "./pages/ContactProfilePage";
import CompaniesPage from "./pages/CompaniesPage";
import { CompanyProfilePage } from "./pages/CompanyProfilePage";
import DealsPage from "./pages/DealsPage";
import LeadsPage from "./pages/LeadsPage";
// ProspectsPage removed - functionality merged into ProspectingPage
import TicketsPage from "./pages/TicketsPage";
import CampaignsPage from "./pages/CampaignsPage";
import ProductsPage from "./pages/ProductsPage";
import WarrantiesPage from "./pages/WarrantiesPage";
import WarrantyRegionsPage from "./pages/WarrantyRegionsPage";
import PortalAnalyticsPage from "./pages/PortalAnalyticsPage";
import SendNotificationPage from "./pages/SendNotificationPage";
import ResourceManagementPage from "./pages/ResourceManagementPage";
import TasksPage from "./pages/TasksPage";
import UsersPage from "./pages/UsersPage";
import SequencesPage from "./pages/SequencesPage";
import OrdersPage from "./pages/OrdersPage";
import MemosPage from "./pages/MemosPage";
import EngagementCampaignsPage from "./pages/EngagementCampaignsPage";
import EmailTemplatesPage from "./pages/EmailTemplatesPage";
import PortalLoginPage from "./pages/PortalLoginPage";
import PortalDashboardPage from "./pages/PortalDashboardPage";
import BookMeetingPage from "./pages/BookMeetingPage";
import BrandingSettingsPage from "./pages/BrandingSettingsPage";
import AgentSettingsPage from "./pages/AgentSettingsPage";
import MyDashboardPage from "./pages/MyDashboardPage";
import CalendarPage from "./pages/CalendarPage";
import TeamManagementPage from "./pages/TeamManagementPage";
import ReportsPage from "./pages/ReportsPage";
import ResourceDocumentsPage from "./pages/ResourceDocumentsPage";
import EmailSettingsPage from "./pages/EmailSettingsPage";
import SignatureSettingsPage from "./pages/SignatureSettingsPage";
import ManagerDashboardPage from "./pages/ManagerDashboardPage";
import HolidaySettingsPage from "./pages/HolidaySettingsPage";
import LiveChatSettingsPage from "./pages/LiveChatSettingsPage";
import AgentChatDashboardPage from "./pages/AgentChatDashboardPage";
import ChatAnalyticsPage from "./pages/ChatAnalyticsPage";
import EmbedWidgetPage from "./pages/EmbedWidgetPage";
import ContactImportExportPage from "./pages/ContactImportExportPage";

// Marketing Suite Pages
import MarketingDashboardPage from "./pages/MarketingDashboardPage";
import MarketingCampaignsPage from "./pages/MarketingCampaignsPage";
import CampaignDetailPage from "./pages/CampaignDetailPage";
import CampaignEditPage from "./pages/CampaignEditPage";
import ContentCalendarPage from "./pages/ContentCalendarPage";
import WorkflowsPage from "./pages/WorkflowsPage";
import WorkflowManagementPage from "./pages/WorkflowManagementPage";
import LegalRulesPage from "./pages/LegalRulesPage";
import MarketingIdeationPage from "./pages/MarketingIdeationPage";
import CRMSettingsPage from "./pages/CRMSettingsPage";
import CustomerSegmentsPage from "./pages/CustomerSegmentsPage";
import BrandVoiceSettingsPage from "./pages/BrandVoiceSettingsPage";
import MarketingReportingPage from "./pages/MarketingReportingPage";
import ABTestingPage from "./pages/ABTestingPage";
import MarketingCreativePage from "./pages/MarketingCreativePage";
import AssetRepositoryPage from "./pages/AssetRepositoryPage";
import DesignManagerPage from "./pages/DesignManagerPage";
import CampaignTemplatesPage from "./pages/CampaignTemplatesPage";
import LandingPagesListPage from "./pages/LandingPagesListPage";
import LandingPageBuilderPage from "./pages/LandingPageBuilderPage";
import LandingPageAnalyticsPage from "./pages/LandingPageAnalyticsPage";
import MarketingAutomationPage from "./pages/MarketingAutomationPage";
import SocialMediaSettingsPage from "./pages/SocialMediaSettingsPage";
import ExecutiveDashboardPage from "./pages/ExecutiveDashboardPage";
import AIReportBuilderPage from "./pages/AIReportBuilderPage";
import SEOToolsPage from "./pages/SEOToolsPage";
import CampaignArchivesPage from "./pages/CampaignArchivesPage";
import ScheduledReportsPage from "./pages/ScheduledReportsPage";
import CompareCampaignsPage from "./pages/CompareCampaignsPage";
import MarketingReportsPage from "./pages/MarketingReportsPage";
import OmniChannelPage from "./pages/OmniChannelPage";

// Commerce Pages
import CommerceOverviewPage from "./pages/CommerceOverviewPage";
import SalesQuotesPage from "./pages/SalesQuotesPage";

// Sales Pages
import SalesDashboardPage from "./pages/SalesDashboardPage";
import SalesWorkflowRulesPage from "./pages/SalesWorkflowRulesPage";
import ProspectingPage from "./pages/ProspectingPage";
import LeadSourcesPage from "./pages/LeadSourcesPage";
import MarketingRequestsPage from "./pages/MarketingRequestsPage";
import AIInsightsPage from "./pages/AIInsightsPage";
import SalesWorkspacePage from "./pages/SalesWorkspacePage";
import EmailSequencesPage from "./pages/EmailSequencesPage";
import SalesForecastingPage from "./pages/SalesForecastingPage";
import SalesPlaybookPage from "./pages/SalesPlaybookPage";
import SalesCampaignsPage from "./pages/SalesCampaignsPage";
import MarketResearchPage from "./pages/MarketResearchPage";
import FinanceDashboardPage from "./pages/FinanceDashboardPage";
import BudgetBuilderPage from "./pages/BudgetBuilderPage";
import ServiceHubPage from "./pages/ServiceHubPage";
import HRHubPage from "./pages/HRHubPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import AIWorkforcePage from "./pages/AIWorkforcePage";
import AIWorkspacePage from "./pages/AIWorkspacePage";
import LeadershipAIHubPage from "./pages/LeadershipAIHubPage";
import FileLibraryPage from "./pages/FileLibraryPage";
import SecurityCentrePage from "./pages/SecurityCentrePage";
import LegalPage from "./pages/LegalPage";
import DataHubPage from "./pages/DataHubPage";
import OperationsHubPage from "./pages/OperationsHubPage";
import ModuleConfigPage from "./pages/ModuleConfigPage";
import BoardDashboardPage from "./pages/BoardDashboardPage";
import ManagementDashboardPage from "./pages/ManagementDashboardPage";
import CustomDashboardBuilderPage from "./pages/CustomDashboardBuilderPage";
import VoIPSettingsPage from "./pages/VoIPSettingsPage";
import CommunicationsHubPage from "./pages/CommunicationsHubPage";
import CustomerPortalAdminPage from "./pages/CustomerPortalAdminPage";
import BugReportCenterPage from "./pages/BugReportCenterPage";
import SystemDocumentationPage from "./pages/SystemDocumentationPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Customer Portal Pages
import CustomerLoginPage from "./pages/CustomerLoginPage";
import CustomerPortalHomePage from "./pages/CustomerPortalHomePage";
import CustomerPortalFilesPage from "./pages/CustomerPortalFilesPage";
import CustomerPortalTestimonialsPage from "./pages/CustomerPortalTestimonialsPage";
import CustomerPortalTicketsPage from "./pages/CustomerPortalTicketsPage";
import CustomerPortalBookMeetingPage from "./pages/CustomerPortalBookMeetingPage";
import CustomerPortalWarrantiesPage from "./pages/CustomerPortalWarrantiesPage";
import CustomerPortalNotificationsPage from "./pages/CustomerPortalNotificationsPage";
import CustomerPortalResourcesPage from "./pages/CustomerPortalResourcesPage";
import AffiliatePortalPage from "./pages/AffiliatePortalPage";
import AffiliateManagementPage from "./pages/AffiliateManagementPage";

// Layouts
import DashboardLayout from "./layouts/DashboardLayout";
import CustomerPortalLayout from "./layouts/CustomerPortalLayout";

// CRM Roles - these roles can access the main CRM dashboard
const CRM_ROLES = ['admin', 'sales_rep', 'sales_manager', 'marketing_manager', 'support_agent', 'technical_support', 'hr_manager', 'finance_manager'];
// Affiliate role - separate portal access
const AFFILIATE_ROLES = ['affiliate'];

// Protected Route Component for CRM (excludes customers)
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user, hasRole } = useAuth();
  const location = useLocation();

  // Track the last visited protected path
  useEffect(() => {
    if (isAuthenticated && location.pathname !== '/login' && location.pathname !== '/register') {
      sessionStorage.setItem('lastVisitedPath', location.pathname);
    }
  }, [isAuthenticated, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Save the attempted URL to redirect after login
    // Redirect unauthenticated users to customer portal login
    return <Navigate to="/customer-login" state={{ from: location }} replace />;
  }

  // Security: Customers cannot access CRM routes - redirect to portal
  if (user?.role === 'customer') {
    return <Navigate to="/my-portal" replace />;
  }

  // Security: Affiliates cannot access CRM routes - redirect to affiliate portal
  if (user?.role === 'affiliate') {
    return <Navigate to="/affiliate" replace />;
  }

  if (allowedRoles && !hasRole(allowedRoles)) {
    return <Navigate to="/my-dashboard" replace />;
  }

  return children;
};

// Customer Portal Protected Route - Only for customers
const CustomerProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/customer-login" state={{ from: location }} replace />;
  }

  // Security: Only customers can access portal, CRM users go to dashboard
  if (user?.role !== 'customer') {
    return <Navigate to="/my-dashboard" replace />;
  }

  return children;
};

// Affiliate Portal Protected Route - Only for affiliates
const AffiliateProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Security: Only affiliates can access affiliate portal
  if (user?.role !== 'affiliate') {
    return <Navigate to="/my-dashboard" replace />;
  }

  return children;
};

// Public Route (redirect to last visited page or dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Customers go to portal
    if (user?.role === 'customer') {
      return <Navigate to="/my-portal" replace />;
    }
    // Affiliates go to affiliate portal
    if (user?.role === 'affiliate') {
      return <Navigate to="/affiliate" replace />;
    }
    // CRM users go to dashboard - Check if there's a stored last path
    const lastPath = sessionStorage.getItem('lastVisitedPath');
    const redirectTo = lastPath && lastPath !== '/login' && lastPath !== '/register' ? lastPath : '/my-dashboard';
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

// Customer Public Route - for customer login/register pages
const CustomerPublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Customers go to portal
    if (user?.role === 'customer') {
      return <Navigate to="/my-portal" replace />;
    }
    // Affiliates go to affiliate portal
    if (user?.role === 'affiliate') {
      return <Navigate to="/affiliate" replace />;
    }
    // CRM users go to my-dashboard
    return <Navigate to="/my-dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Customer Portal Public Routes */}
      <Route path="/customer-login" element={<CustomerPublicRoute><CustomerLoginPage /></CustomerPublicRoute>} />
      <Route path="/customer-register" element={<CustomerPublicRoute><RegisterPage /></CustomerPublicRoute>} />
      
      {/* Customer Portal Protected Routes */}
      <Route path="/my-portal" element={<CustomerProtectedRoute><CustomerPortalLayout /></CustomerProtectedRoute>}>
        <Route index element={<CustomerPortalHomePage />} />
        <Route path="files" element={<CustomerPortalFilesPage />} />
        <Route path="testimonials" element={<CustomerPortalTestimonialsPage />} />
        <Route path="support" element={<CustomerPortalTicketsPage />} />
        <Route path="warranties" element={<CustomerPortalWarrantiesPage />} />
        <Route path="book-meeting" element={<CustomerPortalBookMeetingPage />} />
        <Route path="notifications" element={<CustomerPortalNotificationsPage />} />
        <Route path="resources" element={<CustomerPortalResourcesPage />} />
      </Route>

      {/* Affiliate Portal - For affiliate partners only */}
      <Route path="/affiliate" element={
        <AffiliateProtectedRoute>
          <div className="min-h-screen bg-slate-50">
            <header className="bg-white border-b px-6 py-4">
              <div className="flex items-center justify-between max-w-7xl mx-auto">
                <h1 className="text-xl font-bold">Affiliate Partner Portal</h1>
                <button 
                  onClick={() => {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                  }}
                  className="text-sm text-slate-600 hover:text-slate-900"
                >
                  Logout
                </button>
              </div>
            </header>
            <main className="max-w-7xl mx-auto p-6">
              <AffiliatePortalPage />
            </main>
          </div>
        </AffiliateProtectedRoute>
      } />

      {/* Legacy Portal Routes - Redirect to main customer portal */}
      <Route path="/portal/login" element={<Navigate to="/customer-login" replace />} />
      <Route path="/portal/dashboard" element={<Navigate to="/my-portal" replace />} />
      <Route path="/portal/*" element={<Navigate to="/my-portal" replace />} />

      {/* CRM Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Public Meeting Scheduler */}
      <Route path="/book" element={<BookMeetingPage />} />

      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/my-dashboard" replace />} />
        <Route path="dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'executive', 'board', 'sales_manager', 'marketing_manager', 'finance_manager', 'hr_manager']}>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="contacts" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'marketing_manager', 'support_agent', 'technical_support']}>
            <ContactsPage />
          </ProtectedRoute>
        } />
        <Route path="contacts/import-export" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager', 'team_lead', 'sales_rep']}>
            <ContactImportExportPage />
          </ProtectedRoute>
        } />
        <Route path="contacts/:contactId" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'marketing_manager', 'support_agent', 'technical_support']}>
            <ContactProfilePage />
          </ProtectedRoute>
        } />
        <Route path="companies" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'marketing_manager']}>
            <CompaniesPage />
          </ProtectedRoute>
        } />
        <Route path="companies/:companyId" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'marketing_manager']}>
            <CompanyProfilePage />
          </ProtectedRoute>
        } />
        <Route path="deals" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <DealsPage />
          </ProtectedRoute>
        } />
        <Route path="leads" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'marketing_manager']}>
            <LeadsPage />
          </ProtectedRoute>
        } />
        <Route path="campaigns" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <CampaignsPage />
          </ProtectedRoute>
        } />
        <Route path="tickets" element={<TicketsPage />} />
        <Route path="orders" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'support_agent']}>
            <OrdersPage />
          </ProtectedRoute>
        } />
        <Route path="products" element={
          <ProtectedRoute allowedRoles={['admin', 'support_agent', 'technical_support']}>
            <ProductsPage />
          </ProtectedRoute>
        } />
        <Route path="warranties" element={<WarrantiesPage />} />
        <Route path="warranty-regions" element={
          <ProtectedRoute allowedRoles={['admin', 'support_agent', 'technical_support', 'sales_manager', 'sales_rep', 'manager', 'marketing_manager']}>
            <WarrantyRegionsPage />
          </ProtectedRoute>
        } />
        <Route path="portal-analytics" element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'sales_manager']}>
            <PortalAnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="send-notification" element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'support_agent', 'technical_support']}>
            <SendNotificationPage />
          </ProtectedRoute>
        } />
        <Route path="customer-resources" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager', 'support_agent', 'technical_support']}>
            <ResourceManagementPage />
          </ProtectedRoute>
        } />
        
        {/* Commerce Routes */}
        <Route path="commerce" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'support_agent', 'technical_support']}>
            <CommerceOverviewPage />
          </ProtectedRoute>
        } />
        <Route path="commerce/products" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'support_agent', 'technical_support']}>
            <ProductsPage />
          </ProtectedRoute>
        } />
        <Route path="commerce/quotes" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'support_agent', 'technical_support']}>
            <SalesQuotesPage />
          </ProtectedRoute>
        } />
        <Route path="commerce/warranties" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'support_agent', 'technical_support']}>
            <WarrantiesPage />
          </ProtectedRoute>
        } />
        <Route path="commerce/documentation" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'support_agent', 'technical_support']}>
            <ResourceDocumentsPage />
          </ProtectedRoute>
        } />
        
        {/* Sales Routes */}
        <Route path="sales" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <SalesDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="sales/dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <SalesDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="sales/campaigns" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'marketing_manager']}>
            <SalesCampaignsPage />
          </ProtectedRoute>
        } />
        <Route path="sales/market-research" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <MarketResearchPage />
          </ProtectedRoute>
        } />
{/* Prospects page removed - functionality merged into /sales/prospecting */}
        <Route path="sales/deals" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <DealsPage />
          </ProtectedRoute>
        } />
        <Route path="sales/quotes" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <SalesQuotesPage />
          </ProtectedRoute>
        } />
        <Route path="sales/pipeline" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <DealsPage />
          </ProtectedRoute>
        } />
        <Route path="sales/commerce" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <CommerceOverviewPage />
          </ProtectedRoute>
        } />
        <Route path="sales/commerce/products" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <ProductsPage />
          </ProtectedRoute>
        } />
        <Route path="sales/commerce/warranties" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <WarrantiesPage />
          </ProtectedRoute>
        } />
        <Route path="sales/commerce/documentation" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <ResourceDocumentsPage />
          </ProtectedRoute>
        } />
        <Route path="sales/workflow-rules" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager']}>
            <SalesWorkflowRulesPage />
          </ProtectedRoute>
        } />
        <Route path="sales/prospecting" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <ProspectingPage />
          </ProtectedRoute>
        } />
        <Route path="sales/lead-sources" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager', 'marketing_manager']}>
            <LeadSourcesPage />
          </ProtectedRoute>
        } />
        <Route path="sales/marketing-requests" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'marketing_manager']}>
            <MarketingRequestsPage />
          </ProtectedRoute>
        } />
        <Route path="sales/ai-insights" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager', 'marketing_manager']}>
            <AIInsightsPage />
          </ProtectedRoute>
        } />
        <Route path="sales/workspace" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <SalesWorkspacePage />
          </ProtectedRoute>
        } />
        <Route path="sales/email-sequences" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'marketing_manager']}>
            <EmailSequencesPage />
          </ProtectedRoute>
        } />
        <Route path="sales/forecasting" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <SalesForecastingPage />
          </ProtectedRoute>
        } />
        <Route path="sales/playbooks" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager']}>
            <SalesPlaybookPage />
          </ProtectedRoute>
        } />
        <Route path="sales/affiliates" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager']}>
            <AffiliateManagementPage />
          </ProtectedRoute>
        } />
        
        <Route path="finance" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager']}>
            <FinanceDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="finance/dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager']}>
            <FinanceDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="finance/budget/:budgetId" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager']}>
            <BudgetBuilderPage />
          </ProtectedRoute>
        } />
        
        <Route path="service-hub" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager', 'support_agent', 'technical_support']}>
            <ServiceHubPage />
          </ProtectedRoute>
        } />
        
        <Route path="hr-hub" element={
          <ProtectedRoute allowedRoles={['admin', 'hr_manager', 'sales_manager']}>
            <HRHubPage />
          </ProtectedRoute>
        } />
        <Route path="hr-hub/ai-workforce" element={
          <ProtectedRoute allowedRoles={['admin', 'hr_manager', 'sales_manager']}>
            <AIWorkforcePage />
          </ProtectedRoute>
        } />
        <Route path="ai/leadership" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'marketing_manager', 'support_agent', 'technical_support', 'team_lead']}>
            <LeadershipAIHubPage />
          </ProtectedRoute>
        } />
        <Route path="files" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'marketing_manager', 'support_agent', 'technical_support', 'team_lead']}>
            <FileLibraryPage />
          </ProtectedRoute>
        } />
        <Route path="ai-workspace" element={
          <ProtectedRoute allowedRoles={['admin', 'hr_manager', 'sales_manager', 'marketing_manager']}>
            <AIWorkspacePage />
          </ProtectedRoute>
        } />
        
        <Route path="settings" element={
          <ProtectedRoute allowedRoles={['admin', 'hr_manager', 'sales_manager', 'marketing_manager']}>
            <AdminSettingsPage />
          </ProtectedRoute>
        } />
        
        <Route path="bug-reports" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <BugReportCenterPage />
          </ProtectedRoute>
        } />
        
        <Route path="system-docs" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SystemDocumentationPage />
          </ProtectedRoute>
        } />
        
        <Route path="security" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'marketing_manager', 'support_agent', 'technical_support', 'hr_manager']}>
            <SecurityCentrePage />
          </ProtectedRoute>
        } />
        
        <Route path="legal" element={<LegalPage />} />
        
        <Route path="data-hub" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager', 'marketing_manager']}>
            <DataHubPage />
          </ProtectedRoute>
        } />
        
        <Route path="operations" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <OperationsHubPage />
          </ProtectedRoute>
        } />
        
        <Route path="settings/modules" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <ModuleConfigPage />
          </ProtectedRoute>
        } />
        
        <Route path="settings/voip" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <VoIPSettingsPage />
          </ProtectedRoute>
        } />
        
        <Route path="communications" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager', 'sales_rep', 'marketing_manager', 'support_agent']}>
            <CommunicationsHubPage />
          </ProtectedRoute>
        } />
        
        <Route path="customer-portal-admin" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <CustomerPortalAdminPage />
          </ProtectedRoute>
        } />
        
        <Route path="board-dashboard" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <BoardDashboardPage />
          </ProtectedRoute>
        } />
        
        <Route path="team-dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager', 'marketing_manager', 'hr_manager']}>
            <ManagementDashboardPage />
          </ProtectedRoute>
        } />
        
        <Route path="dashboard-builder" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'sales_manager', 'marketing_manager', 'support_agent', 'technical_support']}>
            <CustomDashboardBuilderPage />
          </ProtectedRoute>
        } />
        
        <Route path="tasks" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'marketing_manager', 'support_agent', 'technical_support']}>
            <TasksPage />
          </ProtectedRoute>
        } />
        <Route path="sequences" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager', 'sales_rep']}>
            <SequencesPage />
          </ProtectedRoute>
        } />
        <Route path="engagement" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <EngagementCampaignsPage />
          </ProtectedRoute>
        } />
        <Route path="email-templates" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <EmailTemplatesPage />
          </ProtectedRoute>
        } />
        <Route path="memos" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'marketing_manager', 'support_agent', 'technical_support']}>
            <MemosPage />
          </ProtectedRoute>
        } />
        <Route path="users" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UsersPage />
          </ProtectedRoute>
        } />
        <Route path="settings/branding" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <BrandingSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="my-dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'marketing_manager', 'support_agent', 'technical_support']}>
            <MyDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="my-settings" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'marketing_manager', 'support_agent', 'technical_support']}>
            <AgentSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="team-management" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <TeamManagementPage />
          </ProtectedRoute>
        } />
        <Route path="reports" element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        } />
        <Route path="resource-documents" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <ResourceDocumentsPage />
          </ProtectedRoute>
        } />
        <Route path="email-settings" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'marketing_manager', 'support_agent', 'technical_support']}>
            <EmailSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="email-signatures" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'marketing_manager', 'support_agent', 'technical_support']}>
            <SignatureSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="manager-dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager', 'team_lead']}>
            <ManagerDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="holiday-settings" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <HolidaySettingsPage />
          </ProtectedRoute>
        } />
        <Route path="live-chat/settings" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <LiveChatSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="live-chat/dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'support_agent', 'technical_support']}>
            <AgentChatDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="live-chat/analytics" element={
          <ProtectedRoute allowedRoles={['admin', 'sales_manager', 'team_lead']}>
            <ChatAnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="live-chat/embed" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <EmbedWidgetPage />
          </ProtectedRoute>
        } />
        
        {/* Marketing Suite Routes */}
        <Route path="marketing" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <MarketingDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/campaigns" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <MarketingCampaignsPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/campaigns/:campaignId" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <CampaignDetailPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/campaigns/:campaignId/edit" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <CampaignEditPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/campaigns/new" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <CampaignEditPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/calendar" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <ContentCalendarPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/workflows" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <WorkflowsPage />
          </ProtectedRoute>
        } />
        <Route path="workflow-management" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager', 'sales_rep', 'sales_manager', 'support_agent', 'technical_support', 'team_lead']}>
            <WorkflowManagementPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/legal-rules" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <LegalRulesPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/ideation" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager', 'sales_rep', 'sales_manager', 'support_agent', 'technical_support', 'team_lead']}>
            <MarketingIdeationPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/reporting" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <MarketingReportingPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/ai-reports" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <AIReportBuilderPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/seo-tools" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <SEOToolsPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/campaign-archives" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <CampaignArchivesPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/compare-campaigns" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <CompareCampaignsPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/scheduled-reports" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <ScheduledReportsPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/marketing-reports" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <MarketingReportsPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/omni-channel" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <OmniChannelPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/ab-testing" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <ABTestingPage />
          </ProtectedRoute>
        } />
        <Route path="settings/crm" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <CRMSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="settings/segments" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <CustomerSegmentsPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/brand-voice" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <BrandVoiceSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/creative" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <MarketingCreativePage />
          </ProtectedRoute>
        } />
        <Route path="marketing/asset-repository" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <AssetRepositoryPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/design-manager" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <DesignManagerPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/templates" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <CampaignTemplatesPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/landing-pages" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <LandingPagesListPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/landing-pages/:pageId" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <LandingPageBuilderPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/landing-page-analytics" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <LandingPageAnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="marketing/automation" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <MarketingAutomationPage />
          </ProtectedRoute>
        } />
        <Route path="settings/social-media" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <SocialMediaSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="executive-dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'marketing_manager']}>
            <ExecutiveDashboardPage />
          </ProtectedRoute>
        } />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/my-dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ModuleProvider>
        <BrowserRouter>
          <TutorialProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </TutorialProvider>
        </BrowserRouter>
      </ModuleProvider>
    </AuthProvider>
  );
}

export default App;
